"use client";

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ActiveProject from "../../../assets/icons/Region.svg";
import {
  getActualBudgetReport,
  getAllUnits,
} from "../../../redux/unitSlice/unitSlice";
import ConsolidatedReportsTable from "../../../components/ConsolidatedReportsTable/ConsolidatedReportsTable";
import CustomSelectWithFilter from "../../../components/Common/CustomSelect/CustomSelectWithFilter";

const parseNumber = (v) => Number(v?.toString().replace(/,/g, "")) || 0;

const formatNumber = (val) =>
  val !== null && val !== undefined
    ? val.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "--";

const ActualBudgetReport = () => {
  const dispatch = useDispatch();

  const { allUnitsList, actualProjectWiseProjectDetails, loading } = useSelector(
    (state) => state.unit
  );

  const [selectedUnitRegion, setSelectedUnitRegion] =
    useState("Select Unit/Region");

  useEffect(() => {
    if (selectedUnitRegion !== "Select Unit/Region") {
      dispatch(getActualBudgetReport({ mnf_unit: selectedUnitRegion }));
    } else {
      dispatch(getActualBudgetReport({}));
    }

    dispatch(getAllUnits());
  }, [dispatch, selectedUnitRegion]);

  const data = actualProjectWiseProjectDetails || {};
  const summary = data?.summary || {};
  const fy2526 = data?.FY2526 || {};
  const fy2627 = data?.FY2627 || {};

  /* =========================
     COLUMN STRUCTURE
  ========================= */

  const columns = [
    {
      title: "Parameter",
      dataIndex: "label",
      fixed: "left",
      width: 220,
    },
    {
      title: "Actual FY 2024-25",
      dataIndex: "actual_2425",
      align: "right",
      width: 200
    },
    {
      title: "Budget FY 2025-26",
      dataIndex: "budget_2526",
      align: "right",
      width: 200
    },
    {
      title: "FY 2025-26",
      children: [
        { title: "Upto Q3", dataIndex: "actual_upto_q3_2526", align: "right" },
        { title: "For Q4", dataIndex: "for_q4_2526", align: "right" },
        { title: "Total", dataIndex: "total_2526", align: "right" },
        { title: "Variation", dataIndex: "variation_2526", align: "right" },
      ],
    },
    {
      title: "FY 2026-27",
      children: [
        { title: "For Q1", dataIndex: "q1_2627", align: "right" },
        { title: "For Q2", dataIndex: "q2_2627", align: "right" },
        { title: "For Q3", dataIndex: "q3_2627", align: "right" },
        { title: "For Q4", dataIndex: "q4_2627", align: "right" },
        { title: "Total", dataIndex: "total_2627", align: "right" },
        { title: "Variation", dataIndex: "variation_2627", align: "right" },
      ],
    },
  ];

  /* =========================
     METRICS
  ========================= */

  const metrics = [
    { key: "revenue", label: "Revenue" },
    { key: "DM_COST", label: "Material Cost" },
    { key: "DM_GTO", label: "DM % (on GTO)" },
    { key: "DM_REV", label: "DM % (on Revenue)" },
    { key: "projectCost", label: "Total Cost" },
    { key: "projectProfit", label: "Margin (A-B)" },
    { key: "totalNetBilling", label: "Net Billing" },
    { key: "totalCashInflow", label: "Cash Inflow" },
    { key: "netCashFlow", label: "Cash Netflow" },
  ];

  /* =========================
     TABLE DATA
  ========================= */

  const tableData = (() => {
    const dm = (cost, revenue) => (revenue ? (cost / revenue) * 100 : 0);

    const fmtNum = (v) => formatNumber(v ?? 0);
    const fmtPct = (v) => `${(v ?? 0).toFixed(2)}%`;

    const rows = metrics
      .filter((m) => !["DM_GTO", "DM_REV"].includes(m.key))
      .map((metric) => {
        const actual2425 = parseNumber(
          summary?.[metric.key]?.FY_2024_25_ACTUAL
        );

        const budget2526 = parseNumber(
          summary?.[metric.key]?.FY_2025_26_BUDGET
        );

        const totalFromApi = parseNumber(fy2526?.[metric.key]?.Total);

        const q4_2526 = parseNumber(fy2526?.[metric.key]?.["Q4-2025"]);

        const actualUptoQ3 = totalFromApi - q4_2526;

        const total2526 = actualUptoQ3 + q4_2526;

        const q1 = parseNumber(fy2627?.[metric.key]?.["Q1-2026"]);
        const q2 = parseNumber(fy2627?.[metric.key]?.["Q2-2026"]);
        const q3 = parseNumber(fy2627?.[metric.key]?.["Q3-2026"]);
        const q4 = parseNumber(fy2627?.[metric.key]?.["Q4-2026"]);

        const total2627 = q1 + q2 + q3 + q4;

        return {
          key: metric.key,
          label: metric.label,

          actual_2425: actual2425,
          budget_2526: budget2526,

          actual_upto_q3_2526: actualUptoQ3,
          for_q4_2526: q4_2526,
          total_2526: total2526,
          variation_2526: total2526 - budget2526,

          q1_2627: q1,
          q2_2627: q2,
          q3_2627: q3,
          q4_2627: q4,

          total_2627: total2627,
          variation_2627: total2627 - total2526,
        };
      });

    const revenueRow = rows.find((r) => r.key === "revenue");
    const dmCostRow = rows.find((r) => r.key === "DM_COST");

    const dmGtoRow = {
      key: "DM_GTO",
      label: "DM % (on GTO)",

      actual_2425: dm(dmCostRow.actual_2425, revenueRow.actual_2425),
      budget_2526: dm(dmCostRow.budget_2526, revenueRow.budget_2526),

      actual_upto_q3_2526: dm(
        dmCostRow.actual_upto_q3_2526,
        revenueRow.actual_upto_q3_2526
      ),
      for_q4_2526: dm(dmCostRow.for_q4_2526, revenueRow.for_q4_2526),
      total_2526: dm(dmCostRow.total_2526, revenueRow.total_2526),

      variation_2526: "-",

      q1_2627: dm(dmCostRow.q1_2627, revenueRow.q1_2627),
      q2_2627: dm(dmCostRow.q2_2627, revenueRow.q2_2627),
      q3_2627: dm(dmCostRow.q3_2627, revenueRow.q3_2627),
      q4_2627: dm(dmCostRow.q4_2627, revenueRow.q4_2627),

      total_2627: dm(dmCostRow.total_2627, revenueRow.total_2627),
      variation_2627: "-",
    };

    const dmRevenueRow = {
      key: "DM_REV",
      label: "DM % (on Revenue)",

      actual_2425: dm(dmCostRow.actual_2425, revenueRow.actual_2425),
      budget_2526: dm(dmCostRow.budget_2526, revenueRow.budget_2526),

      actual_upto_q3_2526: dm(
        dmCostRow.actual_upto_q3_2526,
        revenueRow.actual_upto_q3_2526
      ),
      for_q4_2526: dm(dmCostRow.for_q4_2526, revenueRow.for_q4_2526),
      total_2526: dm(dmCostRow.total_2526, revenueRow.total_2526),

      variation_2526: "-",

      q1_2627: dm(dmCostRow.q1_2627, revenueRow.q1_2627),
      q2_2627: dm(dmCostRow.q2_2627, revenueRow.q2_2627),
      q3_2627: dm(dmCostRow.q3_2627, revenueRow.q3_2627),
      q4_2627: dm(dmCostRow.q4_2627, revenueRow.q4_2627),

      total_2627: dm(dmCostRow.total_2627, revenueRow.total_2627),
      variation_2627: "-",
    };

    const gtoIndex = metrics.findIndex((m) => m.key === "DM_GTO");
    const revIndex = metrics.findIndex((m) => m.key === "DM_REV");

    rows.splice(gtoIndex, 0, dmGtoRow);
    rows.splice(revIndex, 0, dmRevenueRow);

    return rows.map((r) => {
      const isDM = ["DM_GTO", "DM_REV"].includes(r.key);

      const formatCell = (v) =>
        isDM && typeof v === "number"
          ? fmtPct(v)
          : typeof v === "number"
          ? fmtNum(v)
          : v;

      return {
        ...r,
        actual_2425: formatCell(r.actual_2425),
        budget_2526: formatCell(r.budget_2526),
        actual_upto_q3_2526: formatCell(r.actual_upto_q3_2526),
        for_q4_2526: formatCell(r.for_q4_2526),
        total_2526: formatCell(r.total_2526),
        variation_2526: formatCell(r.variation_2526),
        q1_2627: formatCell(r.q1_2627),
        q2_2627: formatCell(r.q2_2627),
        q3_2627: formatCell(r.q3_2627),
        q4_2627: formatCell(r.q4_2627),
        total_2627: formatCell(r.total_2627),
        variation_2627: formatCell(r.variation_2627),
      };
    });
  })();

  return (
    <div className="p-5 ">
   
         <div className="flex gap-3 items-center">
                <img src={ActiveProject} alt="region" />
                <div>
                   <div className="text-[24px] font-semibold">
                     {/* {sessionStorage.getItem("role")==="ADMIN" ? "BHEL" : JSON.parse(sessionStorage.getItem("user"))?.email.split("_").join(" ")} Level Performance Plan - Actual Budget Report */}
                     Performance Plan - Comparative
                  </div>
                  <div className="text-gray-500 text-sm">
                    * All figures are in Rupees (in Crores)
                  </div>
                </div>
              </div>
      

      <div className="mt-4">
        <div className="flex justify-end gap-3 mb-4">
          {sessionStorage.getItem("role")?.toLowerCase() === "admin" && (
            <CustomSelectWithFilter
              showSearch="true"
              data={
                Array.isArray(allUnitsList)
                  ? allUnitsList.map((value) => ({
                      label: value?.UNIT_DISPLAY_NAME,
                      value: value?.MNF_UNIT,
                    }))
                  : []
              }
              onChange={(value) => setSelectedUnitRegion(value)}
              value={selectedUnitRegion}
              placeholder="Select Unit/Region"
              className="w-[15rem]"
            />
          )}
        </div>

        <ConsolidatedReportsTable
          columns={columns}
          dataSource={tableData}
          loading={loading}
          bordered
          scroll={{ x: "max-content", y: 800 }}
          sticky
          pagination={false}
        />
      </div>
    </div>
  );
};

export default ActualBudgetReport;